import { SignalTies } from '@cadstrom/cadstrom-shared';
import Chip from '@mui/material/Chip';

import { ModelText } from '#components/model-text/model-text.js';
import { SignalTieChip } from '#src/components/signal-tie-chip/signal-tie-chip.js';
import { signalDirectionLabels } from '#src/misc/signal-direction-labels.js';
import { isDefined } from '#src/utils/is-defined.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { ATTRIBUTE_INDICATORS } from './utils.js';

import type { HilSignal } from '@cadstrom/backend/shared';

export interface SignalTooltipContentProps {
	signal: HilSignal;
	showAttributes?: boolean;
	allSignals: HilSignal[];
}

/**
 * The formatted hover-card body for a signal — name, sigma-model signal, direction, attributes,
 * repeatable-block info, description, floating behaviour, and ties. Tie target names are resolved
 * against `allSignals`.
 */
export function SignalTooltipContent({ signal, showAttributes = true, allSignals }: SignalTooltipContentProps) {
	const el = useDataElTagGenerator(SignalTooltipContent);

	const activeAttributes = Object.entries(signal.attributes)
		.filter(([, value]) => value === true)
		.map(([key]) => key);

	return (
		<div {...el()} className='flex flex-col gap-3'>
			{/* Signal name */}
			<div className='border-b border-gray-a6 pb-2'>
				<ModelText value={signal.name} className='text-base font-semibold' />
			</div>

			{/* Sigma Model signal */}
			{isDefined(signal.sigmaModelSignal) && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Sigma Model signal</p>
					<p className='font-mono text-sm'>{signal.sigmaModelSignal}</p>
				</div>
			)}

			{/* Direction */}
			{isDefined(signal.direction) && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Direction</p>
					<p className='text-sm'>{signalDirectionLabels[signal.direction]}</p>
				</div>
			)}

			{/* Attributes */}
			{showAttributes && activeAttributes.length > 0 && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Attributes</p>
					<div className='flex flex-wrap gap-1'>
						{activeAttributes.map((attr) => {
							const indicator = ATTRIBUTE_INDICATORS[attr];
							return (
								<span
									key={attr}
									className='rounded px-1.5 py-0.5 text-xs font-medium'
									style={{
										backgroundColor: indicator?.color ?? '#6b7280',
										color: 'white',
										boxShadow: indicator ? `0 0 8px ${indicator.shadowColor}` : undefined,
									}}
								>
									{formatAttributeName(attr)}
								</span>
							);
						})}
					</div>
				</div>
			)}

			{/* Block info */}
			{isDefined(signal.blockInfo) && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Repeatable block</p>
					<div className='text-sm'>
						{isDefined(signal.blockInfo.name) && (
							<p>
								<span className='text-gray-a11'>Name:</span> {signal.blockInfo.name}
							</p>
						)}
						{isDefined(signal.blockInfo.blockPattern) && (
							<p>
								<span className='text-gray-a11'>Pattern:</span> {signal.blockInfo.blockPattern}
							</p>
						)}
						<p>
							<span className='text-gray-a11'>Index:</span>{' '}
							<span className='tabular-nums'>
								{String.fromCharCode('A'.charCodeAt(0) + signal.blockInfo.index)}
								<span className='text-gray-a11'> / </span>
								{signal.blockInfo.index + 1}
							</span>
						</p>
					</div>
				</div>
			)}

			{/* Description */}
			{isDefined(signal.description) && signal.description !== '' && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Description</p>
					<p className='text-sm leading-relaxed'>{signal.description}</p>
				</div>
			)}

			{/* Floating behaviour */}
			{isDefined(signal.floatingBehavior) && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Floating behaviour</p>
					<p className='text-sm'>{formatFloatingBehavior(signal.floatingBehavior)}</p>
				</div>
			)}

			{/* Ties */}
			{isDefined(signal.ties) && signal.ties.length > 0 && (
				<div className='flex flex-col gap-1'>
					<p className='text-2xs font-semibold tracking-wide text-gray-a11 uppercase'>Ties</p>
					<div className='flex flex-wrap gap-1'>
						{signal.ties.map((tie, index) => {
							const parsedTie = SignalTies.convertExternalComponentToFlatSignalTie(tie);
							if (parsedTie === null) {
								return <Chip key={index} color='gray' label='Unknown' />;
							}

							const toSignalName =
								allSignals.find((s) => String(s.id) === parsedTie.toSignalId)?.name ?? parsedTie.toSignalId;
							// Key by index: multiple ties to the same target must not share a React key.
							return <SignalTieChip key={index} tie={parsedTie} target={<ModelText value={toSignalName} />} />;
						})}
					</div>
				</div>
			)}
		</div>
	);
}

/**
 * Formats attribute names to be more human-readable.
 */
function formatAttributeName(attr: string): string {
	const attributeLabels: Record<string, string> = {
		power: 'Power',
		ground: 'Ground',
		analog: 'Analog',
		digital: 'Digital',
		exposedPad: 'Exposed Pad',
		noConnect: 'No Connect',
		activeLow: 'Active Low',
	};

	return attributeLabels[attr] ?? attr;
}

/**
 * Formats floating behavior enum values to be more human-readable.
 */
function formatFloatingBehavior(behavior: string): string {
	const behaviorLabels: Record<string, string> = {
		never: 'Never',
		sometimes: 'Sometimes',
		always: 'Always',
	};

	return behaviorLabels[behavior] ?? behavior;
}
